import { useBnbSelector } from "../../redux/store";
import type { PromotionType } from "../../../../../common-deprecated/types/CommonTypes";
import { useBnbOptionalLabel } from "../../utils/labels";
import useExternalPriceTaxDisclaimer from "./useExternalPriceTaxDisclaimer";
import { showLegalBannerDisclaimer } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { LegalBannerDisclaimerDataType } from "../../../../shared-logic/types/FinanceTypes";
import { createFinanceMinMax, financeTableToObject } from "../../../../shared-logic/utils/legalBannerDisclaimerUtils";
import { showAemMonthlyRates } from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";

export type ParsedDisclaimerType = {
    index?: number;
    id: string;
    name?: string;
    from?: string;
    until?: string;
    disclaimer?: string;
    collapsible?: boolean;
    isLexus?: boolean;
    collapseMobileDisclaimers: boolean;
};
export type DisclaimerContentReturnType = {
    items: ParsedDisclaimerType[];
    legalBannerDisclaimerData: LegalBannerDisclaimerDataType | null;
};

const useDisclaimerContent = (
    showGenericFinanceDisclaimer: boolean,
    isPdf: boolean = false,
): DisclaimerContentReturnType => {
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const disclaimerPromotions = useBnbSelector((state) => state.items.disclaimerPromotions);
    const promotions = useBnbSelector((state) => state.items.promotions);
    const financeDisclaimer = useBnbSelector((state) => state.finance.info.disclaimer);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const hasLegalBannerDisclaimer = useBnbSelector((state) => showLegalBannerDisclaimer(state.commonSettings));
    const modelCode = useBnbSelector((state) => state.model.internalCode);
    const monthlyEnabled = showAemMonthlyRates(commonSettings, AemFmComponent.Build, "new", modelCode);
    const modelStepGrades = useBnbSelector((state) => state.model.grades);
    const gradeStepGrades = useBnbSelector((state) => state.grades.list);
    const gradeStepMotorizations = useBnbSelector((state) => state.grade.motorizations);
    const { collapsePromoDisclaimers, collapseMobileDisclaimers } = bnbSettings;
    const { hidePromotionDisclaimers, showPrintDisclaimer } = bnbSettings;
    const { visible } = useExternalPriceTaxDisclaimer();

    const [
        printDisclaimerLabel,
        genericDisclaimerLabel,
        genericFinanceDisclaimerLabel,
        genericPdfDisclaimer,
        basketTaxDisclaimerContent,
        basketTaxDisclaimerTitle,
    ] = useBnbOptionalLabel([
        "printDisclaimer",
        "genericDisclaimer",
        "genericFinanceDisclaimer",
        "genericPdfDisclaimer",
        "basketTaxDisclaimerContent",
        "basketTaxDisclaimerTitle",
    ]);

    // Helper function to get the correct disclaimer object from a promotion.
    const getPromotionData = (disclaimerPromotion: PromotionType, promos: PromotionType[]): ParsedDisclaimerType => ({
        index: promos.find((promotion) => promotion.id === disclaimerPromotion.id)?.index || 1,
        id: disclaimerPromotion.id,
        name: disclaimerPromotion.name,
        from: disclaimerPromotion.from,
        until: disclaimerPromotion.until,
        disclaimer: disclaimerPromotion.disclaimer,
        collapseMobileDisclaimers: false,
    });

    const disclaimers: ParsedDisclaimerType[] = hidePromotionDisclaimers
        ? []
        : disclaimerPromotions.map((promo) => ({
              ...getPromotionData(promo, promotions),
              collapseMobileDisclaimers,
              collapsible: collapsePromoDisclaimers,
          }));

    if (hidePromotionDisclaimers) {
        const userSelectedPromotions = promotions.filter((promo) => promo.customerOptInRequired);
        userSelectedPromotions.forEach((promo) => {
            disclaimers.push({
                ...getPromotionData(promo, promotions),
                collapseMobileDisclaimers,
                collapsible: collapsePromoDisclaimers,
            });
        });
    }

    // Check if the print disclaimer should be shown. This print disclaimer should be referenced from the same label as used in the print page to show the disclaimer.
    if ((showPrintDisclaimer || isPdf) && printDisclaimerLabel) {
        disclaimers.unshift({
            id: "printDisclaimer",
            disclaimer: printDisclaimerLabel,
            collapseMobileDisclaimers,
        });
    }

    // Check if the genericDisclaimer is available.
    if (genericDisclaimerLabel) {
        disclaimers.unshift({
            id: "genericDisclaimer",
            disclaimer: genericDisclaimerLabel,
            collapseMobileDisclaimers,
        });
    }

    // Check if the generic finance disclaimer should be shown.
    if (genericFinanceDisclaimerLabel && financeOption === "monthly" && showGenericFinanceDisclaimer) {
        disclaimers.unshift({
            id: "genericFinanceDisclaimer",
            disclaimer: genericFinanceDisclaimerLabel,
            collapseMobileDisclaimers,
        });
    }

    const monthlyRateDisclaimer =
        (!hasLegalBannerDisclaimer && (bnbSettings.showMonthlyRatesFootnote || !isPdf) && financeDisclaimer?.value) ||
        null;
    if (monthlyRateDisclaimer) {
        disclaimers.push({
            id: "",
            disclaimer: monthlyRateDisclaimer,
            collapseMobileDisclaimers,
        });
    }

    let legalBannerDisclaimerData: LegalBannerDisclaimerDataType | null = null;
    if (monthlyEnabled && hasLegalBannerDisclaimer) {
        // Model step, need to show a range depending on the bodytype grade combinations.
        if (modelStepGrades.length) {
            legalBannerDisclaimerData = createFinanceMinMax(modelStepGrades);
        }
        if (gradeStepGrades.length) {
            legalBannerDisclaimerData = createFinanceMinMax(gradeStepGrades);
        }
        // Grade step (= engine selection step), need to show a range depending on the engines.
        if (gradeStepMotorizations.length) {
            legalBannerDisclaimerData = createFinanceMinMax(gradeStepMotorizations);
        }
        // Customize and onward only need to show one value
        if (financeDisclaimer?.table) {
            legalBannerDisclaimerData = {
                finance: [financeTableToObject(financeDisclaimer.table)],
                minMax: { min: financeDisclaimer.table },
                disclaimer: financeDisclaimer.value,
            };
        }
    }

    if (genericPdfDisclaimer) {
        disclaimers.push({
            id: "genericPdfDisclaimer",
            disclaimer: genericPdfDisclaimer,
            collapseMobileDisclaimers,
        });
    }

    if (visible && basketTaxDisclaimerContent && basketTaxDisclaimerTitle) {
        disclaimers.push({
            id: `${basketTaxDisclaimerTitle}-${disclaimers.filter((a) => a.index).length + 1}`,
            disclaimer: basketTaxDisclaimerContent,
            collapseMobileDisclaimers,
            index: disclaimers.filter((a) => a.index).length + 1,
            name: basketTaxDisclaimerTitle,
        });
    }

    // Filter out double disclaimers
    const uniqueDisclaimers = disclaimers.filter(
        (disclaimer, index) => disclaimers.findIndex((d) => d.id === disclaimer.id) === index,
    );
    return { items: uniqueDisclaimers, legalBannerDisclaimerData };
};

export default useDisclaimerContent;
